import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ROLES = [
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'operador', label: 'Operador' },
  { value: 'revisor', label: 'Revisor' },
  { value: 'consulta', label: 'Consulta' },
];

const ROLE_BADGE = {
  supervisor: 'bg-blue-950/50 text-blue-300 border-blue-800/50',
  operador: 'bg-emerald-950/50 text-emerald-300 border-emerald-800/50',
  revisor: 'bg-violet-950/50 text-violet-300 border-violet-800/50',
  consulta: 'bg-slate-800/50 text-slate-400 border-slate-700/50',
};

const ROLE_DESC = {
  supervisor: 'Acesso total — gerencia usuários, classes, configurações e todos os dados.',
  operador: 'Cria e edita instrumentos, registra calibrações e altera status.',
  revisor: 'Visualiza e assina laudos de calibração.',
  consulta: 'Somente leitura — sem permissão de edição.',
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--surface-4)]">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-lg leading-none">✕</button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

const INPUT = 'w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500 outline-none focus:border-blue-500';

const ADMIN_EMAIL = 'brunoruotulo@gmail.com';

export default function UsersPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.email === ADMIN_EMAIL;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modais
  const [modalCreate, setModalCreate] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);    // user object
  const [modalReset, setModalReset] = useState(null);  // user object

  // Formulário criar
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fPassword, setFPassword] = useState('');
  const [fRole, setFRole] = useState('consulta');
  const [fError, setFError] = useState('');
  const [fSaving, setFSaving] = useState(false);

  // Formulário editar
  const [eName, setEName] = useState('');
  const [eRole, setERole] = useState('');
  const [eError, setEError] = useState('');
  const [eSaving, setESaving] = useState(false);

  // Redefinir senha
  const [rPassword, setRPassword] = useState('');
  const [rError, setRError] = useState('');
  const [rSaving, setRSaving] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar os usuários.');
    } finally {
      setLoading(false);
    }
  };

  // ── Criar usuário ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setFName(''); setFEmail(''); setFPassword(''); setFRole('consulta'); setFError('');
    setModalCreate(true);
  };

  const handleCreate = async () => {
    if (!fName.trim() || !fEmail.trim() || !fPassword) { setFError('Preencha todos os campos.'); return; }
    if (fPassword.length < 6) { setFError('A senha deve ter pelo menos 6 caracteres.'); return; }
    setFSaving(true); setFError('');
    try {
      const res = await api.post('/auth/users', { name: fName.trim(), email: fEmail.trim(), password: fPassword, role: fRole });
      setUsers(prev => [...prev, res.data]);
      setModalCreate(false);
    } catch (err) {
      setFError(err.response?.data?.error || 'Erro ao criar usuário.');
    } finally {
      setFSaving(false);
    }
  };

  // ── Editar usuário ─────────────────────────────────────────────────────────
  const openEdit = (u) => {
    setEName(u.name || ''); setERole(u.role); setEError('');
    setModalEdit(u);
  };

  const handleEdit = async () => {
    if (!eName.trim()) { setEError('Nome obrigatório.'); return; }
    setESaving(true); setEError('');
    try {
      const res = await api.patch(`/auth/users/${modalEdit.id}`, { name: eName.trim(), role: eRole });
      setUsers(prev => prev.map(u => u.id === modalEdit.id ? { ...u, ...res.data } : u));
      setModalEdit(null);
    } catch (err) {
      setEError(err.response?.data?.error || 'Erro ao salvar.');
    } finally {
      setESaving(false);
    }
  };

  // ── Toggle ativo/inativo ────────────────────────────────────────────────────
  const handleToggleActive = async (u) => {
    try {
      const res = await api.patch(`/auth/users/${u.id}/toggle-active`);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, active: res.data.active } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao alterar status.');
    }
  };

  // ── Redefinir senha ────────────────────────────────────────────────────────
  const openReset = (u) => { setRPassword(''); setRError(''); setModalReset(u); };

  const handleReset = async () => {
    if (rPassword.length < 6) { setRError('Mínimo de 6 caracteres.'); return; }
    setRSaving(true); setRError('');
    try {
      await api.patch(`/auth/users/${modalReset.id}/reset-password`, { password: rPassword });
      setModalReset(null);
    } catch (err) {
      setRError(err.response?.data?.error || 'Erro ao redefinir senha.');
    } finally {
      setRSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-400">Carregando...</div>;

  return (
    <div className="p-8 max-w-4xl">

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuários</h1>
          <p className="text-sm text-slate-500 mt-1">Gerencie o acesso da sua equipe ao CalibraAI</p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            + Novo Usuário
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-950/40 border border-red-700/50 rounded-lg text-red-300 text-sm">{error}</div>
      )}

      {/* Legenda de roles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {ROLES.map(r => (
          <div key={r.value} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-3">
            <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border mb-1 ${ROLE_BADGE[r.value]}`}>
              {r.label}
            </span>
            <p className="text-[10px] text-slate-500 leading-relaxed">{ROLE_DESC[r.value]}</p>
          </div>
        ))}
      </div>

      {/* Tabela de usuários */}
      <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--surface-4)]">
              <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 px-4 py-3">Usuário</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 px-4 py-3">Perfil</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--surface-4)]">
            {users.map(u => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className={`transition-colors hover:bg-[var(--surface-4)]/40 ${!u.active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-white">{u.name || '—'}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                    {isMe && <span className="text-[9px] text-blue-400">você</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${ROLE_BADGE[u.role]}`}>
                      {ROLES.find(r => r.value === u.role)?.label || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${u.active ? 'text-emerald-400' : 'text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      {u.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(u)}
                        className="text-xs px-2 py-1 text-slate-400 hover:text-white hover:bg-[var(--surface-4)] rounded transition-colors"
                        title="Editar nome e perfil"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => openReset(u)}
                        className="text-xs px-2 py-1 text-slate-400 hover:text-amber-400 hover:bg-[var(--surface-4)] rounded transition-colors"
                        title="Redefinir senha"
                      >
                        🔑
                      </button>
                      {!isMe && (
                        <button
                          onClick={() => handleToggleActive(u)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            u.active
                              ? 'text-slate-400 hover:text-red-400 hover:bg-[var(--surface-4)]'
                              : 'text-slate-400 hover:text-emerald-400 hover:bg-[var(--surface-4)]'
                          }`}
                          title={u.active ? 'Desativar conta' : 'Reativar conta'}
                        >
                          {u.active ? '🚫' : '✅'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="py-12 text-center text-slate-500 text-sm">Nenhum usuário encontrado.</div>
        )}
      </div>

      {/* ── Modal: Criar usuário ────────────────────────────────────────────── */}
      {modalCreate && (
        <Modal title="Novo Usuário" onClose={() => setModalCreate(false)}>
          <Field label="Nome completo *">
            <input type="text" value={fName} onChange={e => setFName(e.target.value)} placeholder="Ex: João Silva" className={INPUT} autoFocus />
          </Field>
          <Field label="E-mail *">
            <input type="email" value={fEmail} onChange={e => setFEmail(e.target.value)} placeholder="joao@empresa.com" className={INPUT} />
          </Field>
          <Field label="Senha inicial *">
            <input type="password" value={fPassword} onChange={e => setFPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className={INPUT} />
          </Field>
          <Field label="Perfil de acesso">
            <select value={fRole} onChange={e => setFRole(e.target.value)} className={INPUT}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">{ROLE_DESC[fRole]}</p>
          </Field>
          {fError && <p className="text-red-400 text-xs">{fError}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setModalCreate(false)} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
              Cancelar
            </button>
            <button onClick={handleCreate} disabled={fSaving} className="px-4 py-2 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 transition-colors">
              {fSaving ? 'Criando...' : 'Criar usuário'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Editar usuário ───────────────────────────────────────────── */}
      {modalEdit && (
        <Modal title={`Editar — ${modalEdit.email}`} onClose={() => setModalEdit(null)}>
          <Field label="Nome completo">
            <input type="text" value={eName} onChange={e => setEName(e.target.value)} className={INPUT} autoFocus />
          </Field>
          <Field label="Perfil de acesso">
            <select
              value={eRole}
              onChange={e => setERole(e.target.value)}
              disabled={modalEdit.id === me?.id}
              className={`${INPUT} disabled:opacity-50`}
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">{ROLE_DESC[eRole]}</p>
            {modalEdit.id === me?.id && (
              <p className="text-[10px] text-amber-500 mt-1">Não é possível alterar seu próprio perfil.</p>
            )}
          </Field>
          {eError && <p className="text-red-400 text-xs">{eError}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setModalEdit(null)} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
              Cancelar
            </button>
            <button onClick={handleEdit} disabled={eSaving} className="px-4 py-2 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 transition-colors">
              {eSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Redefinir senha ──────────────────────────────────────────── */}
      {modalReset && (
        <Modal title={`Redefinir senha — ${modalReset.name || modalReset.email}`} onClose={() => setModalReset(null)}>
          <p className="text-xs text-slate-400">
            Defina uma nova senha para o usuário. Comunique a senha ao usuário por canal seguro.
          </p>
          <Field label="Nova senha">
            <input
              type="password" value={rPassword} onChange={e => setRPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres" className={INPUT} autoFocus
            />
          </Field>
          {rError && <p className="text-red-400 text-xs">{rError}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setModalReset(null)} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
              Cancelar
            </button>
            <button onClick={handleReset} disabled={rSaving} className="px-4 py-2 text-xs bg-amber-700 hover:bg-amber-600 text-white rounded-lg disabled:opacity-50 transition-colors">
              {rSaving ? 'Salvando...' : 'Redefinir senha'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
